import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, MoreHorizontal } from 'lucide-react';
import GlassTabBar from '../ui/GlassTabBar.jsx';
import Modal from '../ui/Modal.jsx';
import { useAuth } from '../../contexts/AuthContext';
import triggerHaptic from '../../utils/haptics';
import {
  getMobileNavItems,
  getOverflowNavGroups,
  isNavItemActive
} from '../../config/dashboardNav';

/**
 * Navigation des Dashboards auf dem Telefon.
 *
 * Ersetzt seit September 2026 den Knopf "Dashboard-Menue" und das Schubfach
 * dahinter. Anlass war das Onboarding einer Pilotschule: Ein neuer Schul-Admin
 * hat lange gesucht, wie er aus der Verwaltung zurueck in die App kommt — der
 * Weg zurueck war im Grundzustand des Dashboards ueberhaupt nicht sichtbar,
 * sondern lag hinter dem Hamburger-Knopf.
 *
 * Dieselbe Glasleiste wie in der Haupt-App (`ui/GlassTabBar`), also dasselbe
 * Material, dieselbe Hoehe und derselbe Freiraum (`--bottom-nav-clearance`).
 * Fuenf Ziele: drei Rollen-Ziele, "Mehr" fuer den Rest, und der Ausgang.
 *
 * Der Ausgang ist bewusst NICHT lila. Sichtbarkeit kommt hier aus Platz und
 * Trennlinie; das Farbbudget des Bildschirms (CD v2.0: hoechstens drei lila
 * Elemente) gehoert dem aktiven Ziel und dem "Zur App"-Knopf im Kopf.
 *
 * Haptik (Oktober 2026): Ein Zielwechsel ist ein Wertwechsel und gibt den
 * weichen `selection`-Tick — aber nur, wenn das Ziel nicht schon aktiv ist.
 * Vorher gab jeder Tipp einen leichten Impuls, der Ausgang einen mittleren.
 */
const DashboardBottomNav = () => {
  const location = useLocation();
  const { getUserRole } = useAuth();
  const role = getUserRole();
  const [moreOpen, setMoreOpen] = useState(false);

  const primaryItems = getMobileNavItems(role);
  const overflowGroups = getOverflowNavGroups(role);

  // Rollen ohne Verwaltungsbereich (STUDENT) bekommen keine Leiste. Sie haben
  // im Dashboard ohnehin nichts zu sehen; App.js schickt sie nach /app zurueck.
  if (primaryItems.length === 0) return null;

  const overflowItems = overflowGroups.flatMap((group) => group.items);
  // "Mehr" leuchtet mit, wenn die aktuelle Seite hinter ihm liegt — sonst waere
  // man auf z. B. /dashboard/admin/skills in einer Leiste ohne jeden Anhaltspunkt.
  const moreActive = overflowItems.some((item) => isNavItemActive(location.pathname, item));

  const items = [
    ...primaryItems.map((item) => ({
      key: item.path,
      icon: item.icon,
      label: item.shortName,
      ariaLabel: item.name,
      to: item.path,
      active: isNavItemActive(location.pathname, item),
      onClick: () => {
        if (!isNavItemActive(location.pathname, item)) triggerHaptic('selection');
      }
    })),
    {
      key: 'more',
      icon: MoreHorizontal,
      label: 'Mehr',
      ariaLabel: 'Weitere Bereiche',
      active: moreActive,
      onClick: () => {
        if (!moreActive) triggerHaptic('selection');
        setMoreOpen(true);
      }
    },
    {
      key: 'app',
      icon: Home,
      label: 'App',
      ariaLabel: 'Zurück zur App',
      to: '/app',
      muted: true,
      separatorBefore: true,
      // Der Ausgang ist nie das aktive Ziel (er führt aus dem Dashboard hinaus).
      onClick: () => triggerHaptic('selection')
    }
  ];

  return (
    <>
      <GlassTabBar items={items} ariaLabel="Dashboard-Navigation" className="md:hidden" />

      {/* `ui/Modal` ist auf dem Telefon bereits ein Blatt von unten und bringt
          ueber das native <dialog> Escape, Fokusfang, Fokus-Rueckgabe und die
          Scroll-Sperre mit — genau die drei Dinge, die das alte handgebaute
          Schubfach muehsam nachbauen musste. */}
      <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="Alle Bereiche" size="md" bodyClassName="p-3">
        <nav aria-label="Weitere Bereiche" className="space-y-4">
          {overflowGroups.map((group) => (
            <div key={group.label || 'start'}>
              {group.label && (
                <h3 className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </h3>
              )}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isNavItemActive(location.pathname, item);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => {
                        if (!active) triggerHaptic('selection');
                        setMoreOpen(false);
                      }}
                      aria-current={active ? 'page' : undefined}
                      // Aktiv in Tinte auf Chip-Fläche (Oktober 2026), wie die
                      // Leiste selbst: Wo man ist, ist kein Aufruf zum Handeln.
                      className={`flex items-center gap-3 min-h-[48px] px-3 rounded-xl text-base transition-colors duration-fast ${
                        active
                          ? 'bg-chip text-foreground font-medium'
                          : 'text-muted-foreground hover:bg-foreground/5'
                      }`}
                    >
                      <Icon className="w-5 h-5 shrink-0" aria-hidden="true" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Dritter Weg zurueck. Kostet nichts und faengt den Fall ab, dass
              jemand den Ausgang in der Leiste fuer ein Symbol ohne Ziel haelt. */}
          <div className="border-t border-border pt-3">
            <Link
              to="/app"
              onClick={() => {
                triggerHaptic('selection');
                setMoreOpen(false);
              }}
              className="flex items-center gap-3 min-h-[48px] px-3 rounded-xl text-base font-medium text-foreground hover:bg-foreground/5 transition-colors duration-fast"
            >
              Zurück zur App
            </Link>
          </div>
        </nav>
      </Modal>
    </>
  );
};

export default DashboardBottomNav;
